import cookies from 'browser-cookies';
import { Adp, AdpAnalytics } from '@adsk/adp-web-analytics-sdk';
import {
  BERNOULLI_ADP_BY_ENV,
  BERNOULLI_ADP_KEY,
} from '@/config/adp.site-config';

const AUTH_TOKEN_COOKIE_KEY = 'auth0.user.token';
const TENANT_ID_SESSION_KEY = 'tenantId';

type JwtPayload = {
  sub?: string;
};

type RuntimeAdpConfig = {
  name?: string;
  id?: string;
  id_provider?: 'appkey' | 'cls' | 'o2' | 'upi1' | 'upi2';
  key?: string;
  env?: 'dev' | 'stg' | 'prd';
  region?: string;
};

let bernoulliAdp: AdpAnalytics | null = null;
let bernoulliLoaded = false;
let bernoulliConsentInitialized = false;
let bernoulliConsentReady: Promise<void> | null = null;

export const ADP_EVENTS = {
  APP_ENTRY: 'bernoulli.app.entered',
  APP_FOREGROUND_DURATION: 'bernoulli.app.foregroundDuration',
  NAV_NEW_CHAT: 'bernoulli.nav.newChat',
  CHAT_SEND_MESSAGE: 'bernoulli.chat.sendMessage',
  CHAT_UPLOAD_OPEN: 'bernoulli.chat.upload.openPicker',
  CHAT_HISTORY_OPEN: 'bernoulli.chat.history.openConversation',
  CHAT_HISTORY_DELETE: 'bernoulli.chat.history.deleteConversation',
  CHAT_HISTORY_CLEAR: 'bernoulli.chat.history.clearAll',
} as const;

const getBernoulliProductConfig = (runtimeConfig?: RuntimeAdpConfig) => {
  const env = runtimeConfig?.env ?? 'dev';
  return BERNOULLI_ADP_BY_ENV[env];
};

const getOrCreateBernoulliAdp = () => {
  if (bernoulliAdp) return bernoulliAdp;
  bernoulliAdp = Adp();
  return bernoulliAdp;
};

export const initBernoulliAdp = (params?: {
  runtimeConfig?: RuntimeAdpConfig;
  hostReferrer?: { id?: string; name?: string; sessionId?: string };
}) => {
  const adp = getOrCreateBernoulliAdp();
  const bernoulliConfig = getBernoulliProductConfig(params?.runtimeConfig);

  if (!bernoulliLoaded) {
    adp.setEnv(bernoulliConfig.env);
    adp.load({
      name: bernoulliConfig.name,
      id: bernoulliConfig.id,
      id_provider: bernoulliConfig.id_provider,
      key: bernoulliConfig.key,
      meta: {
        env: bernoulliConfig.env,
        region: bernoulliConfig.region,
      },
    });
    bernoulliLoaded = true;
  }

  if (!bernoulliConsentInitialized) {
    const userToken = cookies.get(AUTH_TOKEN_COOKIE_KEY) || undefined;
    bernoulliConsentReady = adp
      .loadUserConsentPreferences(userToken)
      .then(() => undefined)
      .catch(() => undefined);
    bernoulliConsentInitialized = true;
  }

  if (
    params?.hostReferrer?.id &&
    params.hostReferrer.name &&
    params.hostReferrer.sessionId
  ) {
    adp.setProductReferrer(
      {
        id: params.hostReferrer.id,
        name: params.hostReferrer.name,
        sessionId: params.hostReferrer.sessionId,
      },
      'web_application',
    );
  }

  return adp;
};

export const getBernoulliAdp = () => bernoulliAdp;

const sendAdpTrack = (
  adp: AdpAnalytics,
  eventType: 'CLICK_COMPONENT' | 'APPLICATION_VIEW',
  payload: {
    type: string;
    name: string;
    attributes: Record<string, string>;
  },
) => {
  if (bernoulliConsentReady) {
    void bernoulliConsentReady.finally(() => {
      adp.track(eventType, payload);
    });
    return;
  }

  adp.track(eventType, payload);
};

const decodeJwtPayload = (token: string): JwtPayload | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payloadPart = parts[1];
  if (!payloadPart) return null;

  try {
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const payload = atob(padded);
    return JSON.parse(payload) as JwtPayload;
  } catch {
    return null;
  }
};

export const getForgeUserId = (): string | undefined => {
  const token = cookies.get(AUTH_TOKEN_COOKIE_KEY);
  if (!token) return undefined;

  const payload = decodeJwtPayload(token);
  return payload?.sub;
};

export const getTenantId = (): string | undefined => {
  const tenantId = sessionStorage.getItem(TENANT_ID_SESSION_KEY);
  return tenantId || undefined;
};

const baseAttributes = (extra: Record<string, string> = {}) => {
  const attributes: Record<string, string> = {
    app: 'bernoulli',
    ...extra,
  };

  const forgeUserId = getForgeUserId();
  if (forgeUserId) {
    attributes.user_id = forgeUserId;
  }

  const tenantId = getTenantId();
  if (tenantId) {
    attributes.tenant_id = tenantId;
  }

  return attributes;
};

export const trackAdpEvent = (
  adp: AdpAnalytics | null,
  eventName: string,
  attributes: Record<string, string> = {},
) => {
  if (!adp) return;

  const payload = {
    type: 'command',
    name: 'Bernoulli',
    attributes: baseAttributes({
      event_name: eventName,
      ...attributes,
    }),
  };
  sendAdpTrack(adp, 'CLICK_COMPONENT', payload);
};

export const trackAdpAppView = (
  adp: AdpAnalytics | null,
  attributes: Record<string, string> = {},
) => {
  if (!adp) return;

  const payload = {
    type: 'view',
    name: 'Bernoulli',
    attributes: baseAttributes({
      event_name: ADP_EVENTS.APP_ENTRY,
      ...attributes,
    }),
  };
  sendAdpTrack(adp, 'APPLICATION_VIEW', payload);
};
